import {E, GetEntries} from "js-vextensions";
import moment from "moment";
import React from "react";
import {Button, CheckBox, Column, Row, Select, Spinner, Text, TextInput} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import {Observer, RunInAction_Set, TextPlus} from "web-vcore";
import {engineEndedSessions, GetLiveLocalSession_Reactive} from "../../../../Engine/LiveSessionManagement.js";
import {AddJournalEntry} from "../../../../Server/Commands/JournalEntryCommands.js";
import {JournalEntry} from "../../../../Server/DB/journalEntries/@JournalEntry.js";
import {store} from "../../../../Store/index.js";
import {FlashConfig, OverlayPanel} from "../../../../Store/main/tools/journey.js";
import {observer_mgl} from "mobx-graphlink";

export const JourneyViewSettingsUI = observer_mgl(()=>{
	const uiState = store.main.engine.journey;
	const liveLocalSession = GetLiveLocalSession_Reactive();
	return (
		<Column>
			<Row>
				<Text>Prior journal-entries to show:</Text>
				<Spinner ml={5} value={uiState.pastJournalEntriesToShow} onChange={val=>RunInAction_Set(()=>uiState.pastJournalEntriesToShow = val)}/>
			</Row>
			<Row>
				<Text>Show anchor-entities:</Text>
				<CheckBox ml={5} value={uiState.showAnchorEntities} onChange={val=>RunInAction_Set(()=>uiState.showAnchorEntities = val)}/>
			</Row>
			<Row>
				<Text>Show event boxes:</Text>
				<CheckBox ml={5} value={uiState.showEventBoxes} onChange={val=>RunInAction_Set(()=>uiState.showEventBoxes = val)}/>
			</Row>
			<Row>
				<Text>Show dream-entities:</Text>
				<CheckBox ml={5} value={uiState.showDreamEntities} onChange={val=>RunInAction_Set(()=>uiState.showDreamEntities = val)}/>
			</Row>
			<Row>
				<Text>Screenless entity-adding:</Text>
				<CheckBox ml={5} value={uiState.allowScreenlessEntityAdding} onChange={val=>RunInAction_Set(()=>uiState.allowScreenlessEntityAdding = val)}/>
			</Row>
			<Row>
				<Text>Show bottom-panel:</Text>
				<CheckBox ml={5} value={uiState.showBottomPanel} onChange={val=>RunInAction_Set(()=>uiState.showBottomPanel = val)}/>
			</Row>
			<Row>
				<Text>Overlays:</Text>
				<Button ml={5} size={32} mdIcon="lock" onClick={()=>{
					if (uiState.overlay == "none") {
						RunInAction_Set(()=>uiState.overlay = OverlayPanel.normal);
					}
				}}/>
				<Button ml={5} size={32} mdIcon="lock-smart" onClick={()=>{
					if (uiState.overlay == "none") {
						RunInAction_Set(()=>uiState.overlay = OverlayPanel.canvas);
					}
				}}/>
				<Button ml={5} size={32} mdIcon="door" onClick={()=>{
					if (uiState.overlay == "none") {
						RunInAction_Set(()=>uiState.overlay = OverlayPanel.doorway);
					}
				}}/>
				<Text ml={5}>Default:</Text>
				<Select ml={5} options={GetEntries(OverlayPanel, "ui")} value={uiState.overlay_default}
					onChange={val=>RunInAction_Set(()=>uiState.overlay_default = val)}/>
			</Row>
			<Row>
				<Text>Overlay, snooze duration:</Text>
				<Spinner ml={5} value={uiState.overlay_snoozeDuration} onChange={val=>RunInAction_Set(()=>uiState.overlay_snoozeDuration = val)}/>
				<Text>s</Text>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					Number meanings:
					* intensity
					* flashInterval (in ms)
					* switchInterval (in ms)
					* xOffset
					* yOffset
					* zOffset
				`.AsMultiline(0)}>Flash:</TextPlus>
				<TextInput ml={5} style={{flex: 1}} value={FlashConfig.ToValsArray(uiState.flashConfig).join(",")} onChange={val=>{
					RunInAction_Set(()=>{
						const parts = val.split(",");
						const numbers = parts.map(a=>parseFloat(a));
						if (numbers.length != 6 || numbers.Any(a=>isNaN(a))) return;
						const newConfig = FlashConfig.FromValsArray(numbers);
						uiState.flashConfig = newConfig;
					});
				}}/>
			</Row>
			<Row mt={5}>
				<Button p="1px 7px" text="Upload recent session" enabled={engineEndedSessions.length > 0} onClick={async()=>{
					const recentSessions = [...engineEndedSessions];
					let selectedRecentSession = recentSessions.LastOrX();

					const controller = ShowMessageBox({
						title: "Select recent session", cancelButton: true,
						message: ()=>(
							<Column style={{padding: `10px 0`, width: 600}}>
								<Select displayType="dropdown"
									options={recentSessions.map(session=>({
										name: `${moment(session.coreData.startTime).format("YYYY-MM-DD HH:mm:ss")} - ${session.endTime ? moment(session.endTime).format("YYYY-MM-DD HH:mm:ss") : "n/a"}`,
										value: session,
									}))}
									value={selectedRecentSession}
									onChange={val=>{
										selectedRecentSession = val;
										controller.UpdateUI();
									}}/>
							</Column>
						),
						onOK: async()=>{
							const localSessionToUpload = selectedRecentSession?.AsLocal;
							if (localSessionToUpload == null) {
								return void ShowMessageBox({title: "Cannot upload session", message: "Only local sessions can be uploaded. (selected session is not local)"});
							}
							localSessionToUpload.FinalizeStoredData(false, true);
						},
					});
				}}/>
			</Row>
			<Row mt={5}>
				<Button p="1px 7px" text="Add journal entry" onClick={async()=>{
					const entry = new JournalEntry(E(
						{
							title: "New entry",
							// we don't know if user's adding the entry before or after sleep, so fill in both fields (user can then delete/modify the one that is wrong; till then, the equality indicates auto-entry/uncertainty)
							sleepTime: Date.now(),
							wakeTime: Date.now(),
							segments: [],
						} as Partial<JournalEntry>,
						// if session active, assume user's adding entry for it, so copy session's start-time to also be journal-entry's start-time (and set end-time to null, since session is ongoing)
						liveLocalSession != null && {
							sleepTime: liveLocalSession.coreData.startTime,
							wakeTime: null,
						},
					));
					const entryID = await new AddJournalEntry({entry}).RunOnServer();
				}}/>
			</Row>
		</Column>
	)
});